import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertTriangle, User, Calendar, ChevronDown, MessageSquare } from 'lucide-react';
// Optional external dependencies - will be undefined if not available in package context
let issuesApiService: any;
let toastService: any;
let useAuth: any;

// Optional external dependencies - these will be undefined in package context
// Services can be injected via props or will gracefully degrade
// In package mode, these services are not available and components will work with fallbacks
issuesApiService = undefined;
toastService = undefined;
useAuth = undefined;

// Type fallbacks
type TFormComponent = any;
interface IIssue {
  id?: string;
  title?: string;
  description?: string;
  status?: string;
  priority?: string;
  assignee?: string;
  workOrderNumber?: string;
  assetNumber?: string;
  comments?: string;
  [key: string]: any;
}

enum EIssueStatus {
  OPEN = 'OPEN',
  IN_PROGRESS = 'IN_PROGRESS',
  RESOLVED = 'RESOLVED',
  RESOLVE = 'RESOLVE', // Legacy support
  CLOSED = 'CLOSED',
  REJECTED = 'REJECTED' // Legacy support
}

// Fallback hook if useAuth is not available
const useAuthFallback = () => ({ user: null });
import './RaiseIssueModal.scss';

interface IRaiseIssueModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  component?: TFormComponent;
  formTemplateId?: string;
  notes?: string;
  attachments?: File[] | null;
  issue?: IIssue | null;
}

export const RaiseIssueModal: React.FC<IRaiseIssueModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  component,
  formTemplateId,
  notes = '',
  attachments = null,
  issue = null
}) => {
  const authHook = useAuth || useAuthFallback;
  const { user } = authHook();
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [workOrderNumber, setWorkOrderNumber] = useState<string>('');
  const [assetNumber, setAssetNumber] = useState<string>('');
  const [priority, setPriority] = useState<'High' | 'Medium' | 'Low'>('Medium');
  const [status, setStatus] = useState<string>(EIssueStatus.OPEN);
  const [assignee, setAssignee] = useState<string>('');
  const [comments, setComments] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const isEditMode = !!issue;

  useEffect(() => {
    if (isOpen) {
      if (issue) {
        // Edit mode: populate with issue data
        setTitle(issue.title || '');
        setDescription(issue.description || '');
        setWorkOrderNumber(issue.workOrderNumber || '');
        setAssetNumber(issue.assetNumber || '');
        setPriority((issue.priority as 'High' | 'Medium' | 'Low') || 'Medium');
        // Use enum value or default to OPEN
        const issueStatus = issue.status || EIssueStatus.OPEN;
        // Normalize to enum values if needed (handle legacy values)
        const normalizedStatus = 
          issueStatus === EIssueStatus.OPEN || issueStatus === EIssueStatus.IN_PROGRESS || issueStatus === EIssueStatus.REJECTED || issueStatus === EIssueStatus.RESOLVE
            ? issueStatus
            : EIssueStatus.OPEN;
        setStatus(normalizedStatus);
        setAssignee(issue.assignee || '');
        setComments(issue.comments || '');
      } else if (component) {
        // Create mode: initialize form with component data
        const componentLabel = component.basic?.label || 'Component';
        setTitle(`${componentLabel} - Issue`);
        
        // Build description from component value and notes
        let desc = `Issue raised for component: ${componentLabel}`;
        if (component.basic?.value) {
          desc += `\nComponent Value: ${component.basic.value}`;
        }
        if (notes) {
          desc += `\nNotes: ${notes}`;
        }
        setDescription(desc);
        setComments(notes || '');
      }
    }
  }, [isOpen, component, notes, issue]);

  const handleSubmit = async () => {
    if (!title.trim()) {
      if (toastService?.showError) {
        toastService.showError('Title is required');
      } else {
        console.error('Title is required');
        alert('Title is required');
      }
      return;
    }

    if (!description.trim()) {
      if (toastService?.showError) {
        toastService.showError('Description is required');
      } else {
        console.error('Description is required');
        alert('Description is required');
      }
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditMode && issue) {
        // Update existing issue
        const updateData = {
          title: title.trim(),
          description: description.trim(),
          workOrderNumber: workOrderNumber.trim() || '',
          assetNumber: assetNumber.trim() || '',
          formTemplateId: issue.formTemplateId || '',
          component: issue.component || {},
          priority,
          assignee: assignee.trim() || undefined,
          status: status,
          comments: comments.trim() || ''
        };

        if (issuesApiService?.updateIssue) {
          await issuesApiService.updateIssue(issue._id, updateData);
          if (toastService?.showSuccess) {
            toastService.showSuccess('Issue updated successfully');
          } else {
            console.log('Issue updated successfully');
            alert('Issue updated successfully');
          }
        } else {
          console.warn('Issues API service not available - issue update skipped');
          alert('Issue update functionality not available in package mode');
        }
      } else {
        // Create new issue
        if (!component || !formTemplateId) {
          if (toastService?.showError) {
            toastService.showError('Component and form template are required');
          } else {
            console.error('Component and form template are required');
            alert('Component and form template are required');
          }
          return;
        }

        // Prepare component data for API - include all component properties
        const componentData = {
          ...component,
          basic: {
            ...component.basic,
            comments: notes || ''
          }
        };

        const issueData = {
          title: title.trim(),
          workOrderNumber: workOrderNumber.trim() || undefined,
          assetNumber: assetNumber.trim() || undefined,
          component: componentData,
          description: description.trim(),
          formTemplateId,
          assignee: assignee.trim() || undefined,
          priority,
          status: status,
          comments: comments.trim() || ''
        };

        if (issuesApiService?.createIssue) {
          await issuesApiService.createIssue(issueData, attachments || []);
          if (toastService?.showSuccess) {
            toastService.showSuccess('Issue raised successfully');
          } else {
            console.log('Issue raised successfully');
            alert('Issue raised successfully');
          }
        } else {
          console.warn('Issues API service not available - issue creation skipped');
          alert('Issue creation functionality not available in package mode');
        }
      }
      
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error: any) {
      console.error('Error saving issue:', error);
      const errorMessage = error?.message || error?.error?.message || (isEditMode ? 'Failed to update issue. Please try again.' : 'Failed to raise issue. Please try again.');
      if (toastService?.showError) {
        toastService.showError(errorMessage);
      } else {
        console.error('Error:', errorMessage);
        alert(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const modalContent = (
    <div className="raise-issue-modal-overlay" onClick={onClose}>
      <div className="raise-issue-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="raise-issue-modal-header">
          <div className="raise-issue-modal-header-left">
            <AlertTriangle className="raise-issue-modal-icon" size={20} />
            <div className="raise-issue-modal-header-text">
              <div className="raise-issue-modal-title-main">Issue Details</div>
            </div>
          </div>
          <button
            className="raise-issue-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="raise-issue-modal-content">
          <div className="raise-issue-fields-grid">
            {/* Issue Number - Show only in edit mode */}
            {isEditMode && issue?.issueNumber && (
              <div className="raise-issue-field">
                <label className="raise-issue-field-label">Issue ID</label>
                <input
                  type="text"
                  className="raise-issue-field-input raise-issue-field-readonly"
                  value={issue.issueNumber}
                  readOnly
                />
              </div>
            )}

            {/* Title */}
            <div className="raise-issue-field">
              <label className="raise-issue-field-label">Title</label>
              <input
                type="text"
                className={`raise-issue-field-input ${isEditMode ? 'raise-issue-field-readonly' : ''}`}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter issue title"
                readOnly={isEditMode}
              />
            </div>

            {/* Work Order */}
            <div className="raise-issue-field">
              <label className="raise-issue-field-label">Work Order</label>
              <input
                type="text"
                className={`raise-issue-field-input ${isEditMode ? 'raise-issue-field-readonly' : ''}`}
                value={workOrderNumber}
                onChange={(e) => setWorkOrderNumber(e.target.value)}
                placeholder="N/A"
                readOnly={isEditMode}
              />
            </div>

            {/* Asset */}
            <div className="raise-issue-field">
              <label className="raise-issue-field-label">Asset</label>
              <input
                type="text"
                className={`raise-issue-field-input ${isEditMode ? 'raise-issue-field-readonly' : ''}`}
                value={assetNumber}
                onChange={(e) => setAssetNumber(e.target.value)}
                placeholder="Enter asset number"
                readOnly={isEditMode}
              />
            </div>

            {/* Raised By */}
            <div className="raise-issue-field">
              <label className="raise-issue-field-label">Raised By</label>
              <div className="raise-issue-field-value-with-icon">
                <User size={16} />
                <span>{user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'User' : 'User'}</span>
              </div>
            </div>

            {/* Created On */}
            <div className="raise-issue-field">
              <label className="raise-issue-field-label">Created On</label>
              <div className="raise-issue-field-value-with-icon">
                <Calendar size={16} />
                <span>
                  {isEditMode && issue?.createdAt
                    ? new Date(issue.createdAt).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                      })
                    : new Date().toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                      })}
                </span>
              </div>
            </div>

            {/* Description - Full Width */}
            <div className="raise-issue-field raise-issue-field-full-width">
              <label className="raise-issue-field-label">Description</label>
              <textarea
                className={`raise-issue-field-textarea ${isEditMode ? 'raise-issue-field-readonly' : ''}`}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter issue description"
                rows={4}
                readOnly={isEditMode}
              />
            </div>

            {/* Status */}
            <div className="raise-issue-field">
              <label className="raise-issue-field-label">Status</label>
              <select
                className="raise-issue-field-select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value={EIssueStatus.OPEN}>{EIssueStatus.OPEN}</option>
                <option value={EIssueStatus.IN_PROGRESS}>{EIssueStatus.IN_PROGRESS}</option>
                <option value={EIssueStatus.REJECTED}>{EIssueStatus.REJECTED}</option>
                <option value={EIssueStatus.RESOLVE}>{EIssueStatus.RESOLVE}</option>
              </select>
            </div>

            {/* Assignee */}
            <div className="raise-issue-field">
              <label className="raise-issue-field-label">Assignee</label>
              <div className="raise-issue-assignee-wrapper">
                <input
                  type="text"
                  className="raise-issue-field-input raise-issue-assignee-input"
                  value={assignee}
                  onChange={(e) => setAssignee(e.target.value)}
                  placeholder="Unassigned"
                />
                <ChevronDown size={16} className="raise-issue-assignee-chevron" />
              </div>
            </div>

            {/* Priority */}
            <div className="raise-issue-field raise-issue-field-full-width">
              <label className="raise-issue-field-label">Priority</label>
              <div className="raise-issue-priority-buttons">
                <button
                  type="button"
                  className={`priority-button priority-low ${priority === 'Low' ? 'active' : ''}`}
                  onClick={() => setPriority('Low')}
                >
                  Low
                </button>
                <button
                  type="button"
                  className={`priority-button priority-medium ${priority === 'Medium' ? 'active' : ''}`}
                  onClick={() => setPriority('Medium')}
                >
                  Medium
                </button>
                <button
                  type="button"
                  className={`priority-button priority-high ${priority === 'High' ? 'active' : ''}`}
                  onClick={() => setPriority('High')}
                >
                  High
                </button>
              </div>
            </div>

            {/* Comments - Full Width */}
            <div className="raise-issue-field raise-issue-field-full-width">
              <label className="raise-issue-field-label-with-icon">
                <MessageSquare size={16} />
                <span>Comments</span>
              </label>
              {!comments && (
                <div className="raise-issue-no-comments">No comments yet</div>
              )}
              <textarea
                className={`raise-issue-field-textarea raise-issue-comments-textarea ${isEditMode ? 'raise-issue-field-readonly' : ''}`}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Add a comment..."
                rows={4}
                readOnly={isEditMode}
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="raise-issue-modal-actions">
          <div className="raise-issue-modal-actions-buttons">
            <button
              className="raise-issue-modal-button raise-issue-modal-button-cancel"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              className="raise-issue-modal-button raise-issue-modal-button-save"
              onClick={handleSubmit}
              disabled={isSubmitting || !title.trim() || !description.trim()}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Render modal using portal to document body for full-page overlay
  return createPortal(modalContent, document.body);
};
